import { AgTechDataAction, useDataActionExecutor } from "@agtech/core/data/actions/AgTechDataActions"
import { AgTechApiEntityResponse, convertFromEntityResponse } from "@agtech/core/data/actions/AgTechHttpRequests"
import { useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests"
import { SaleLot } from "../LotData"
import { SaleBidderByNumberIndex } from "../../bidders/BidderData"
import { useCurrentSale } from "../../sales/SaleDataStore"
import { BidderDataGetSaleBiddersQuery } from "../../bidders/BidderDataQueries"
import { LotDataGetLotsQuery } from "../LotDataQueries"
import { InvoiceDataGetUnprocessedSalesQuery } from "../../invoicing/InvoicingDataQueries"
import { useAgTechQuery, waitOnQuery } from "@agtech/core/data/queries/AgTechDataQueries"

/// DTOs

export type LotPurchaseData = {
    lotId: number,
    lotTimestamp: string,
    bidderNumber: string,
    bidderId?: number,
    purchasePrice: number
}

export type LotPurchaseWriteDTO = SaleLot & { bidderNumber?: string }

export type CreateLotPurchaseRequestDTO = {
    lotId: number,
    purchasePrice: number,
    bidderNumber: string,
    bidderId?: number,
    lotTimestamp: string
}

export type UpdateLotPurchaseRequestDTO = {
    newBidderId?: number,
    newBidderNumber?: string,
    newPurchasePrice?: number,
    lotTimestamp: string
}

export type LotPurchaseUpdateDTO = {
    saleId: number,
    bidderId?: number,
    lotId: number,
    bidderNumber: string,
    originalBidderNumber: string,
    purchasePrice: number
}

export type UpdateLotResponseDTO = {
    timestamp: string
}

export type LotPurchaseDeleteDTO = {
    lotId: number,
    bidderNumber: string
}

/// Actions

export const useLotDataWriteLotPurchaseAction = (): AgTechDataAction<LotPurchaseWriteDTO, SaleLot, SaleBidderByNumberIndex> => {
    const actionExecutor = useDataActionExecutor();

    const createLotPurchaseAction = useLotDataCreateLotPurchaseAction();
    const updateLotPurchaseAction = useLotDataUpdateLotPurchaseAction();
    const deleteLotPurchaseAction = useLotDataDeleteLotPurchaseAction();

    const currentSale = useCurrentSale();

    const getBiddersQuery = useAgTechQuery({
        query: BidderDataGetSaleBiddersQuery,
        queryContext: {
            saleId: currentSale.id
        }
    })

    return {
        name: 'WriteLotBidderPurchase',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating lot purchases...',
            actionConfirmationMessage: `Successfully updated lot purchases`,
            shouldLogExecution: false
        }),
        confirm: async props => {
            return props.originalEntity?.bidderNumber !== props.submittedEntity.bidderNumber ||
                   props.originalEntity?.purchasePrice !== props.submittedEntity.purchasePrice;
        },
        validate: async props => {
            const wasLotPreviouslyPurchased = props.originalEntity?.bidderNumber && props.originalEntity?.purchasePrice;

            const doesLotSpecifyBidderNumber = props.submittedEntity?.bidderNumber !== undefined &&
                props.submittedEntity?.bidderNumber !== '';

            const doesLotSpecifyPurchasePrice = props.submittedEntity.purchasePrice !== undefined && !Number.isNaN(props.submittedEntity.purchasePrice) &&
                props.submittedEntity.purchasePrice > 0;

            props.validation.failWithWarningIf(
                !wasLotPreviouslyPurchased && doesLotSpecifyPurchasePrice && !doesLotSpecifyBidderNumber,
                'Please specify a bidder number for this sale',
                () => props.originalEntity ?? props.submittedEntity
            );

            props.validation.failWithWarningIf(
                !wasLotPreviouslyPurchased && doesLotSpecifyBidderNumber && !doesLotSpecifyPurchasePrice,
                'Please specify a valid purchase price',
                () => props.originalEntity ?? props.submittedEntity
            );
        },
        action: async (props) => {
            const wasOriginalLotSold = props.originalEntity?.bidderNumber
                && props.originalEntity?.purchasePrice
                && props.originalEntity?.purchasePrice >= 0;
            
            const isUpdatedLotSold = props.submittedEntity.bidderNumber
                && props.submittedEntity.purchasePrice
                && props.submittedEntity.purchasePrice > 0;

            const hasLotSaleBeenCreated = !wasOriginalLotSold && isUpdatedLotSold;
            const hasLotSaleBeenReversed = wasOriginalLotSold && !isUpdatedLotSold;

            if (hasLotSaleBeenCreated) {
                const lotSellingResult = await actionExecutor.executeAction(createLotPurchaseAction, {
                    submittedEntity: {
                        lot: props.submittedEntity,
                        lotId: props.submittedEntity.id,
                        bidderNumber: props.submittedEntity.bidderNumber ?? '',
                        purchasePrice: props.submittedEntity.purchasePrice ?? 0,
                        lotTimestamp: props.submittedEntity.timestamp
                    }
                });

                return lotSellingResult.success
                    ? { ...lotSellingResult, success: { data: props.submittedEntity }}
                    : lotSellingResult;
            }
            else if (hasLotSaleBeenReversed) {
                const lotSaleDeleteResult = await actionExecutor.executeAction(deleteLotPurchaseAction, {
                    submittedEntity: {
                        lotId: props.submittedEntity.id,
                        bidderNumber: props.originalEntity?.bidderNumber ?? ''
                    }
                });

                return lotSaleDeleteResult.success
                    ? { ...lotSaleDeleteResult, success: { data: {
                        ...props.submittedEntity,
                        bidderNumber: undefined,
                        purchasePrice: undefined
                    }}}
                    : lotSaleDeleteResult;
            }
            else {
                const bidders = await waitOnQuery(getBiddersQuery);

                const updatedBidder = Object.values(bidders).filter(bd => bd.bidderNumber === props.submittedEntity.bidderNumber)[0];

                const updateLotBidderId = props.submittedEntity.bidderNumber
                    ? updatedBidder?.id
                    : undefined;

                const lotSaleUpdateResult = await actionExecutor.executeAction(updateLotPurchaseAction, {
                    originalEntity: {
                        lotId: props.originalEntity?.id ?? 0,
                        lotTimestamp: props.originalEntity?.timestamp ?? '',
                        bidderNumber: props.originalEntity?.bidderNumber ?? '',
                        purchasePrice: props.originalEntity?.purchasePrice ?? 0,
                    },
                    submittedEntity: {
                        lotId: props.submittedEntity.id,
                        lotTimestamp: props.submittedEntity.timestamp,
                        bidderNumber: props.submittedEntity.bidderNumber ?? '',
                        purchasePrice: props.submittedEntity.purchasePrice ?? 0,
                        bidderId: updateLotBidderId
                    }
                });

                return lotSaleUpdateResult.success
                    ? { ...lotSaleUpdateResult, success: { data: props.submittedEntity }}
                    : lotSaleUpdateResult;
            }
        }
    }
}

const useLotDataCreateLotPurchaseAction = (): AgTechDataAction<LotPurchaseData & { lot: SaleLot }, SaleLot> => {
    const currentSale = useCurrentSale();
    const createLotPurchasePostRequest = useAgTechHttpPostRequest<
        CreateLotPurchaseRequestDTO,
        AgTechApiEntityResponse<SaleLot>>();

    return {
        name: 'CreateLotBidderPurchase',
        getConfiguration: () => ({
            actionExecutionMessage: 'Creating lot purchase...',
            actionConfirmationMessage: `Successfully created lot purchase`
        }),
        validate: async props => {
            props.validation.failWithWarningIf(
                props.submittedEntity.bidderNumber === '',
                'Unable to create a lot purchase. The purchasing bidder has not been specified.'
            );
        },
        action: async props => {            
            return await createLotPurchasePostRequest.post({
                path: 'Lot/Sell/' + currentSale.id,
                postedData: {
                    lotId: props.submittedEntity.lotId,
                    lotTimestamp: props.submittedEntity.lot.timestamp,
                    purchasePrice: props.submittedEntity.purchasePrice ?? 0,
                    bidderNumber: props.submittedEntity.bidderNumber ?? '',
                    bidderId: props.submittedEntity.bidderId,
                }
            })
            .then(res => convertFromEntityResponse(res));
        },
        onSuccess: async props => {           
            props.updateQueryData({
                query: LotDataGetLotsQuery,
                queryContext: { saleId: currentSale.id },
                update: lots => {
                    return {
                        ...lots,
                        [props.submittedEntity.lotId]: {
                            ...props.responseData,
                        }
                    };
                }
            });

            props.invalidateQuery({
                query: BidderDataGetSaleBiddersQuery,
                queryContext: { saleId: currentSale.id }
            });

            props.invalidateQuery({
                query: InvoiceDataGetUnprocessedSalesQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    }
}

const useLotDataUpdateLotPurchaseAction = (): AgTechDataAction<LotPurchaseData, SaleLot> => {
    const currentSale = useCurrentSale();
    const updateLotPurchaseRequest = useAgTechHttpPostRequest<UpdateLotPurchaseRequestDTO, AgTechApiEntityResponse<SaleLot>>();

    return {
        name: 'UpdateLotBidderPurchase',
        getConfiguration: () => ({
            actionExecutionMessage: 'Creating lot purchase...',
            actionConfirmationMessage: `Successfully created lot purchase`
        }),
        validate: async props => {
            props.validation.failWithWarningIf(
                !props.submittedEntity.bidderNumber,
                'Please specify a bidder number in order to mark this lot as purchased'
            );

            props.validation.failWithWarningIf(
                !props.submittedEntity.purchasePrice,
                'Please specify a purchase price greater than $0 in order to mark this lot as purchased'
            );
        },
        confirm: async props => {
            return props.originalEntity?.bidderNumber !== props.submittedEntity.bidderNumber ||
                   props.originalEntity?.purchasePrice !== props.submittedEntity.purchasePrice;
        },
        action: async props => {            
            return await updateLotPurchaseRequest.post({
                path: 'Lot/UpdatePurchase/' + props.submittedEntity.lotId,
                postedData: {
                    newBidderId: props.submittedEntity.bidderId,
                    newBidderNumber: props.submittedEntity.bidderNumber,
                    newPurchasePrice: props.submittedEntity.purchasePrice,
                    lotTimestamp: props.submittedEntity.lotTimestamp
                }
            })
            .then(res => convertFromEntityResponse(res));
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: LotDataGetLotsQuery,
                queryContext: { saleId: currentSale.id },
                update: lots => {
                    return {
                        ...lots,
                        [props.submittedEntity.lotId]: {
                            ...props.responseData,
                        }
                    };
                }
            });
            
            props.invalidateQuery({
                query: BidderDataGetSaleBiddersQuery,
                queryContext: { saleId: currentSale.id }
            });

            props.invalidateQuery({
                query: InvoiceDataGetUnprocessedSalesQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    }
}

const useLotDataDeleteLotPurchaseAction = (): AgTechDataAction<{ lotId: number, bidderNumber: string }, SaleLot> => {
    const currentSale = useCurrentSale();
    const deleteLotPurchaseRequest = useAgTechHttpPostRequest<{ lotId: number }, AgTechApiEntityResponse<SaleLot>>();

    return {
        name: 'DeleteLotPurchase',
        getConfiguration: () => ({
            actionExecutionMessage: 'Deleting lot purhcase...',
            actionConfirmationMessage: `Successfully deleted lot purchase`
        }),
        action: async props => {
            return await deleteLotPurchaseRequest.post({
                path: 'Lot/DeletePurchase/' + props.submittedEntity.lotId,
                postedData: {
                    lotId: props.submittedEntity?.lotId ?? 0,
                }
            })
            .then(res => convertFromEntityResponse(res));
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: LotDataGetLotsQuery,
                queryContext: { saleId: currentSale.id },
                update: lots => {
                    return {
                        ...lots,
                        [props.submittedEntity.lotId]: {
                            ...lots[props.submittedEntity.lotId],
                            bidderNumber: undefined,
                            purchasePrice: undefined,
                            originalBidderId: undefined,
                            originalBidder: undefined
                        }
                    }
                }
            });
            
            props.invalidateQuery({
                query: BidderDataGetSaleBiddersQuery,
                queryContext: { saleId: currentSale.id }
            });

            props.invalidateQuery({
                query: InvoiceDataGetUnprocessedSalesQuery,
                queryContext: { saleId: currentSale.id }
            });
        },
        onFailure: async props => {
            props.appContext.messaging.showError({
                header: 'An error occurred while deleting the lot purchase.',
                details: 'Please try again'
            });
        }
    }
}